import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.78);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const Modal = styled(motion.div)`
  position: relative;
  background: ${({ theme }) => (theme.light ? "#ffffff" : "#0e0e0e")};
  border: 1px solid ${({ theme }) => theme.cardBorder};
  width: 100%;
  max-width: 880px;
  max-height: calc(100vh - 40px);
  display: flex;
  overflow: hidden;
  border-radius: 4px;

  @media (max-width: 720px) {
    flex-direction: column;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
  }
`;

export const ImageSide = styled.div`
  position: relative;
  flex: 0 0 46%;
  background: ${({ theme }) => theme.cardBg};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 20%;
    display: block;
  }

  @media (max-width: 720px) {
    flex: 0 0 auto;
    height: 240px;

    img {
      object-position: center 15%;
    }
  }
`;

export const FormSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 36px 32px;
  position: relative;

  @media (max-width: 720px) {
    padding: 28px 22px 24px;
  }
`;

export const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  transition: color 0.2s ease, background 0.2s ease;
  z-index: 2;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.cardBorder};
  }
`;

export const Eyebrow = styled.div`
  font-family: ${({ theme }) => theme.mono};
  font-size: 0.62rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
  margin-bottom: 14px;
`;

export const Title = styled.h2`
  font-family: ${({ theme }) => theme.font};
  font-size: clamp(1.5rem, 2.6vw, 1.9rem);
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.15;
  margin: 0 0 10px;
  color: ${({ theme }) => theme.textPrimary};

  span {
    color: ${({ theme }) => theme.accent};
  }
`;

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0 0 22px;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Input = styled.input`
  width: 100%;
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid ${({ theme }) => theme.cardBorder};
  color: ${({ theme }) => theme.textPrimary};
  padding: 12px 14px;
  font-family: ${({ theme }) => theme.font};
  font-size: 0.9rem;
  outline: none;
  border-radius: 4px;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: ${({ theme }) => theme.accent};
  }

  &::placeholder {
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const SubmitBtn = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.accent};
  color: #000;
  border: none;
  padding: 13px;
  margin-top: 6px;
  font-family: ${({ theme }) => theme.font};
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 4px;
  transition: opacity 0.3s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

export const FinePrint = styled.p`
  font-family: ${({ theme }) => theme.font};
  font-size: 0.72rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.textSecondary};
  text-align: center;
  margin: 16px 0 0;
  opacity: 0.75;

  a {
    color: ${({ theme }) => theme.textPrimary};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

export const SuccessWrap = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
`;

export const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(${({ theme }) => theme.accentRgb}, 0.12);
  border: 2px solid ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.accent};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SuccessMsg = styled.p`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.92rem;
  line-height: 1.6;
  margin: 0;
`;

export const ErrorMsg = styled.div`
  color: #ff6464;
  font-size: 0.82rem;
  text-align: center;
  margin-top: 4px;
`;

export const InfoMsg = styled.div`
  color: ${({ theme }) => theme.accent};
  font-size: 0.82rem;
  text-align: center;
  margin-top: 4px;
`;

export const LangSwitch = styled.div`
  position: absolute;
  top: 18px;
  left: 36px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.mono};
  font-size: 0.65rem;
  letter-spacing: 2px;

  @media (max-width: 720px) {
    left: 22px;
    top: 14px;
  }
`;

export const LangBtn = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  letter-spacing: inherit;
  color: ${({ $active, theme }) =>
    $active ? theme.accent : theme.textSecondary};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  transition: color 0.2s ease;
  &:hover {
    color: ${({ theme }) => theme.accent};
  }
`;

export const LangSep = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.5;
`;
